import * as v from 'valibot'

export const LATEST_WEBCHAT_CONFIG_VERSION = 'v2' as const

export const configSchemaV0 = v.object({
  botName: v.optional(v.string()),
  botAvatar: v.optional(v.string()),
  botDescription: v.optional(v.string()),
  fabImage: v.optional(v.string()),
  phone: v.fallback(v.optional(v.object({ title: v.optional(v.string()), link: v.optional(v.string()) })), undefined),
  email: v.fallback(v.optional(v.object({ title: v.optional(v.string()), link: v.optional(v.string()) })), undefined),
  website: v.fallback(v.optional(v.object({ title: v.optional(v.string()), link: v.optional(v.string()) })), undefined),
  termsOfService: v.fallback(
    v.optional(v.object({ title: v.optional(v.string()), link: v.optional(v.string()) })),
    undefined
  ),
  privacyPolicy: v.fallback(
    v.optional(v.object({ title: v.optional(v.string()), link: v.optional(v.string()) })),
    undefined
  ),
  composerPlaceholder: v.optional(v.string()),
  color: v.optional(v.string()),
  radius: v.optional(v.pipe(v.number(), v.minValue(0.5), v.maxValue(4))),
  themeMode: v.fallback(v.optional(v.picklist(['light', 'dark'])), undefined),
  variant: v.fallback(v.optional(v.picklist(['solid', 'soft'])), undefined),
  fontFamily: v.fallback(v.optional(v.string()), undefined),
  showPoweredBy: v.fallback(v.optional(v.boolean()), undefined),
  additionalStylesheet: v.optional(v.string()),
  additionalStylesheetUrl: v.optional(v.string()),
  allowFileUpload: v.fallback(v.optional(v.boolean()), undefined),
  storageLocation: v.fallback(v.optional(v.picklist(['localStorage', 'sessionStorage'])), undefined),
})

const { showPoweredBy, ...v0EntriesWithoutShowPoweredBy } = configSchemaV0.entries
const configSchemaV1 = v.object({
  ...v0EntriesWithoutShowPoweredBy,
  footer: v.optional(v.string()),
  headerVariant: v.optional(v.picklist(['solid', 'glass'])),
  feedbackEnabled: v.optional(v.boolean()),
})

const configSchemaV2 = v.object({
  ...configSchemaV1.entries,
  soundEnabled: v.optional(v.boolean()),
  adminSecret: v.optional(v.string()),
  embeddedChatId: v.optional(v.string()),
  toggleChatId: v.optional(v.string()),
  proactiveMessageEnabled: v.optional(v.boolean()),
  proactiveBubbleMessage: v.optional(v.string()),
  proactiveBubbleTriggerType: v.optional(v.picklist(['afterDelay', 'userIdle'])),
  proactiveBubbleDelayTime: v.optional(v.pipe(v.number(), v.minValue(0), v.maxValue(60))),
  _debug: v.optional(v.boolean()),
  conversationHistory: v.optional(v.boolean()),
})

export const configSchema = v.union([configSchemaV0, configSchemaV1, configSchemaV2])

export const latestVersionedConfigSchema = v.object({
  ...configSchemaV2.entries,
  version: v.literal('v2'),
})

export const versionedConfigSchema = v.variant('version', [
  //NOTE: Latest needs to be in first or else the parser will always return v0
  latestVersionedConfigSchema,
  v.object({ ...configSchemaV1.entries, version: v.literal('v1') }),
  v.object({ ...configSchemaV0.entries, version: v.fallback(v.literal('v0'), 'v0') }),
])

export const webchatConfigFileSchema = v.object({
  botId: v.string(),
  configuration: versionedConfigSchema,
  clientId: v.string(),
})

export type LatestWebchatConfig = v.InferOutput<typeof configSchemaV2>

export type LatestWebchatTheme = Pick<
  LatestWebchatConfig,
  | 'color'
  | 'fontFamily'
  | 'radius'
  | 'themeMode'
  | 'variant'
  | 'headerVariant'
  | 'additionalStylesheet'
  | 'additionalStylesheetUrl'
>

export type VersionedWebchatConfig = v.InferOutput<typeof versionedConfigSchema>

export type AllPossibleConfigFields = Partial<
  v.InferOutput<typeof configSchemaV0> & v.InferOutput<typeof configSchemaV1> & v.InferOutput<typeof configSchemaV2>
>

export type LatestVersionedWebchatConfig = v.InferOutput<typeof latestVersionedConfigSchema>

export const DEFAULT_TOGGLE_CHAT_ID = 'bp-toggle-chat'

export const DEFAULT_EMBEDDED_CHAT_ID = 'bp-embedded-webchat'

export const initialConfig: Pick<
  LatestWebchatConfig,
  | 'color'
  | 'themeMode'
  | 'radius'
  | 'variant'
  | 'headerVariant'
  | 'fontFamily'
  | 'footer'
  | 'feedbackEnabled'
  | 'soundEnabled'
  | 'proactiveMessageEnabled'
  | 'proactiveBubbleMessage'
  | 'proactiveBubbleTriggerType'
  | 'proactiveBubbleDelayTime'
  | 'conversationHistory'
> = {
  color: '#3276EA',
  themeMode: 'light',
  radius: 4,
  variant: 'solid',
  headerVariant: 'glass',
  fontFamily: 'inter',
  footer: '[⚡ by Botpress](https://botpress.com/?from=webchat)',
  feedbackEnabled: false,
  soundEnabled: false,
  proactiveMessageEnabled: false,
  proactiveBubbleMessage: 'Hi! 👋 Need help?',
  proactiveBubbleTriggerType: 'afterDelay',
  proactiveBubbleDelayTime: 10,
  conversationHistory: false,
}
